import { useState } from "react";
import { HelpCircle, Download, Upload, Search, Tag, Folder, CheckSquare, Star, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

export function HelpDialog() {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-xs" aria-label="Open help documentation">
          <HelpCircle className="h-3 w-3 mr-1" />
          Help
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            TranslateLoo AI Features Guide
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="search" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="search">Search & Filter</TabsTrigger>
            <TabsTrigger value="organize">Organization</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Actions</TabsTrigger>
            <TabsTrigger value="data">Import/Export</TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Enhanced Search & Filtering
              </h3>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">🔍 Smart Search</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Search through both original text and translations in real-time.
                  </p>
                  <Badge variant="outline" className="text-xs">Try searching for words, phrases, or languages</Badge>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Filter className="h-3 w-3" />
                    Advanced Filters
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Language Filters:</strong> Filter by source or target language</li>
                    <li>• <strong>Date Range:</strong> Find translations from specific time periods</li>
                    <li>• <strong>Favorites Only:</strong> Show only your starred translations</li>
                    <li>• <strong>Categories & Tags:</strong> Filter by custom organization labels</li>
                  </ul>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">💡 Pro Tips</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Combine multiple filters for precise results</li>
                    <li>• Use the "Clear All" button to reset quickly</li>
                    <li>• Active filters are shown as removable badges</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="organize" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Folder className="h-4 w-4" />
                Organization System
              </h3>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Folder className="h-3 w-3" />
                    Categories
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Organize translations into categories like "Work", "Travel", "Learning", etc.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="secondary" className="text-xs">📁 Work</Badge>
                    <Badge variant="secondary" className="text-xs">📁 Travel</Badge>
                    <Badge variant="secondary" className="text-xs">📁 Learning</Badge>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Tag className="h-3 w-3" />
                    Tags
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Add multiple tags to each translation for detailed organization.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">#urgent</Badge>
                    <Badge variant="outline" className="text-xs">#formal</Badge>
                    <Badge variant="outline" className="text-xs">#business</Badge>
                    <Badge variant="outline" className="text-xs">#casual</Badge>
                  </div>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">✨ How to Organize</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Click on translation entries to see organization options</li>
                    <li>• Create new categories and tags on-the-fly</li>
                    <li>• Use quick tags from existing ones for faster tagging</li>
                    <li>• Click tags to remove them from entries</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bulk" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <CheckSquare className="h-4 w-4" />
                Bulk Operations
              </h3>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">🎯 Selection Mode</h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Enable bulk selection to manage multiple translations at once.
                  </p>
                  <Badge variant="outline" className="text-xs">Click "Bulk Select" button to start</Badge>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">⚡ Available Actions</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Delete:</strong> Remove multiple translations at once</li>
                    <li>• <strong>Favorites:</strong> Add or remove from favorites in bulk</li>
                    <li>• <strong>Tags:</strong> Add or remove tags from multiple entries</li>
                    <li>• <strong>Categories:</strong> Set categories for selected translations</li>
                  </ul>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">🚀 Quick Tips</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Use "Select All" to select all visible entries</li>
                    <li>• Combine with filters to bulk-edit specific groups</li>
                    <li>• The bulk actions bar appears when selections are made</li>
                    <li>• All actions provide clear confirmation and feedback</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="data" className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Download className="h-4 w-4" />
                Data Management
              </h3>

              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Download className="h-3 w-3" />
                    Export Your Data
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">
                    Download your translation history in multiple formats.
                  </p>
                  <div className="flex gap-2">
                    <Badge variant="secondary" className="text-xs">📄 JSON Format</Badge>
                    <Badge variant="secondary" className="text-xs">📊 CSV Format</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    JSON is best for re-importing, CSV works great with spreadsheets.
                  </p>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Upload className="h-3 w-3" />
                    Import Your Data
                  </h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• <strong>Drag & Drop:</strong> Simply drop files onto the import area</li>
                    <li>• <strong>File Browser:</strong> Click to select files from your device</li>
                    <li>• <strong>Preview:</strong> See what will be imported before confirming</li>
                    <li>• <strong>Validation:</strong> Automatic error detection and reporting</li>
                  </ul>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-medium mb-2">🔒 Data Safety</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Duplicate detection prevents data duplication</li>
                    <li>• Clear error messages help resolve import issues</li>
                    <li>• All data stays local in your browser's storage</li>
                    <li>• Export regularly to backup your translations</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="flex items-center gap-2">
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-muted-foreground">
              Need more help? Check the tooltips throughout the interface!
            </span>
          </div>
          <Button onClick={() => setOpen(false)}>
            Got it!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}